/**
 * Gift-with-purchase: syncs cart lines from rules in #gwp-rules (see sections/gwp-rules.liquid).
 */
(function () {
  const RULES_SCRIPT_ID = "gwp-rules";
  let isGwpSyncing = false;
  let syncTail = Promise.resolve();

  function getCartRoot() {
    if (typeof Shopify !== "undefined" && Shopify.routes && Shopify.routes.root) {
      return String(Shopify.routes.root).replace(/\/?$/, "/");
    }
    return "/";
  }

  function cartPath(suffix) {
    return getCartRoot() + suffix.replace(/^\//, "");
  }

  function isCartEndpointUrl(url) {
    if (!url || typeof url !== "string") {
      return false;
    }
    return (
      url.indexOf("/cart/add") !== -1 ||
      url.indexOf("/cart/change") !== -1 ||
      url.indexOf("/cart/update") !== -1 ||
      url.indexOf("/cart/clear") !== -1
    );
  }

  function requestUrlFromFetchArgs(args) {
    const input = args[0];
    if (typeof input === "string") {
      return input;
    }
    if (typeof Request !== "undefined" && input instanceof Request) {
      return input.url;
    }
    if (input && typeof input.url === "string") {
      return input.url;
    }
    return "";
  }

  function thresholdBasisCents(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }
    let sum = 0;
    for (const item of cart.items) {
      const fp = item.final_line_price;
      if (typeof fp === "number" && !Number.isNaN(fp)) {
        sum += fp;
      }
    }
    let cartLevel = 0;
    const apps = cart.cart_level_discount_applications;
    if (Array.isArray(apps)) {
      for (const d of apps) {
        const a = d.total_allocated_amount;
        if (typeof a === "number" && !Number.isNaN(a)) {
          cartLevel += a;
        }
      }
    }
    const net = sum - cartLevel;
    return net > 0 ? net : 0;
  }

  function parseRules() {
    const el = document.getElementById(RULES_SCRIPT_ID);
    if (!el || !el.textContent) {
      return [];
    }
    try {
      const rules = JSON.parse(el.textContent.trim());
      return Array.isArray(rules) ? rules : [];
    } catch (e) {
      console.warn("[GWP] Invalid rules JSON", e);
      return [];
    }
  }

  function cartHasPrimaryProduct(cart, productId) {
    const pid = String(productId);
    return (cart.items || []).some(function (item) {
      return String(item.product_id) === pid;
    });
  }

  function meetsThreshold(cart, thresholdCents) {
    return thresholdBasisCents(cart) >= thresholdCents;
  }

  function ruleQualified(cart, rule) {
    if (rule.type === "pair") {
      return cartHasPrimaryProduct(cart, rule.primaryProductId);
    }
    if (rule.type === "threshold") {
      return meetsThreshold(cart, rule.thresholdCents);
    }
    return false;
  }

  function findManagedLine(cart, rule) {
    const vid = String(rule.giftVariantId);
    const rid = String(rule.ruleId);
    const items = cart.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.properties || String(item.variant_id) !== vid) {
        continue;
      }
      if (String(item.properties._gwp_rule_id || "") !== rid) {
        continue;
      }
      if (!item.properties._gwp_managed) {
        continue;
      }
      return item;
    }
    return null;
  }

  function fetchCart() {
    return fetch(cartPath("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    }).then(function (res) {
      if (!res.ok) {
        throw new Error("[GWP] cart.js HTTP " + res.status);
      }
      return res.json();
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(body)
    });
  }

  function dispatchCartRefresh() {
    try {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } catch (e) {}
  }

  /**
   * One pass: apply batched line updates, then sequential adds. Returns true if any request succeeded.
   */
  function applyPlan(cart, rules) {
    const updates = {};
    const adds = [];

    for (let r = 0; r < rules.length; r++) {
      const rule = rules[r];
      const qualified = ruleQualified(cart, rule);
      const line = findManagedLine(cart, rule);

      if (!qualified) {
        if (line) {
          updates[line.key] = 0;
        }
        continue;
      }

      if (!line) {
        adds.push(rule);
      } else if (line.quantity !== 1) {
        updates[line.key] = 1;
      }
    }

    const updateKeys = Object.keys(updates);

    return Promise.resolve()
      .then(function () {
        if (!updateKeys.length) {
          return false;
        }
        return postJson(cartPath("cart/update.js"), { updates: updates }).then(function (res) {
          if (!res.ok) {
            console.warn("[GWP] cart/update.js failed", res.status);
            return false;
          }
          return true;
        });
      })
      .then(function (didUpdate) {
        if (!adds.length) {
          return didUpdate;
        }
        return fetchCart().then(function (fresh) {
          let anyAdd = false;

          function addNext(index) {
            if (index >= adds.length) {
              return Promise.resolve(didUpdate || anyAdd);
            }

            const rule = adds[index];

            if (findManagedLine(fresh, rule)) {
              return addNext(index + 1);
            }

            return postJson(cartPath("cart/add.js"), {
              items: [
                {
                  id: rule.giftVariantId,
                  quantity: 1,
                  properties: {
                    _gwp_managed: "1",
                    _gwp_rule: rule.type,
                    _gwp_rule_id: String(rule.ruleId)
                  }
                }
              ]
            })
              .then(function (res) {
                if (!res.ok) {
                  return res
                    .json()
                    .catch(function () {
                      return {};
                    })
                    .then(function (err) {
                      console.warn("[GWP] cart/add.js failed", err);
                      document.dispatchEvent(
                        new CustomEvent("gwp:error", {
                          detail: { rule: rule, error: err }
                        })
                      );
                    });
                }
                anyAdd = true;
                return fetchCart().then(function (c) {
                  fresh = c;
                });
              })
              .then(function () {
                return addNext(index + 1);
              });
          }

          return addNext(0);
        });
      });
  }

  /**
   * Line items for pair rules matching this primary product (same shape as applyPlan adds).
   * Used to batch primary + gifts in one /cart/add.js on landers before checkout redirect.
   */
  function pairGiftLineItemsForProduct(productId) {
    const pid = String(productId || "").trim();
    if (!pid) {
      return [];
    }
    const rules = parseRules();
    const out = [];
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule || rule.type !== "pair" || String(rule.primaryProductId) !== pid) {
        continue;
      }
      const vid = rule.giftVariantId;
      if (vid == null || vid === "") {
        continue;
      }
      out.push({
        id: Number(vid),
        quantity: 1,
        properties: {
          _gwp_managed: "1",
          _gwp_rule: rule.type,
          _gwp_rule_id: String(rule.ruleId)
        }
      });
    }
    return out;
  }

  function syncGwp() {
    const rules = parseRules();
    if (!rules.length) {
      return Promise.resolve();
    }

    const job = syncTail.then(function () {
      isGwpSyncing = true;
      return fetchCart()
        .then(function (cart) {
          return applyPlan(cart, rules);
        })
        .then(function (changed) {
          if (changed) {
            dispatchCartRefresh();
          }
        })
        .catch(function (err) {
          console.warn("[GWP] sync error", err);
        })
        .finally(function () {
          isGwpSyncing = false;
        });
    });

    syncTail = job.catch(function () {});
    return job;
  }

  function scheduleSync() {
    queueMicrotask(function () {
      syncGwp();
    });
  }

  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch;
    window.fetch = function () {
      const result = originalFetch.apply(this, arguments);
      const url = requestUrlFromFetchArgs(arguments);
      if (!isCartEndpointUrl(url)) {
        return result;
      }
      return result.then(function (response) {
        if (response && response.ok && !isGwpSyncing) {
          scheduleSync();
        }
        return response;
      });
    };
  }

  document.addEventListener("cart:change", function () {
    if (!isGwpSyncing) {
      scheduleSync();
    }
  });

  document.addEventListener("DOMContentLoaded", function () {
    scheduleSync();
  });

  window.GWPCart = window.GWPCart || {};
  window.GWPCart.sync = syncGwp;
  window.GWPCart.pairGiftLineItemsForProduct = pairGiftLineItemsForProduct;
})();
